<template>
  <v-container class="people-overview-container standard-content-container">
    <v-row no-gutters>
      <v-col cols="12" md="5">
        <v-row no-gutters class="title-section">
          <v-col
            cols="12"
            sm="2"
            class="link-container"
            :class="{
              'small-container':
                this.$vuetify.breakpoint.width <=
                this.$vuetify.breakpoint.thresholds.xs,
            }"
          >
            <router-link
              :to="{ name: 'OverviewPage', hash: '#your-people' }"
              class="back-arrow"
              ><v-icon large color="mid-grey" class="mt-1">{{
                icons.mdiArrowLeft
              }}</v-icon></router-link
            >
          </v-col>
          <v-col cols="12" sm="10" class="standard-title-container">
            <h1 class="">Your People</h1>
            <p class="your-people-description">
              Tell us about your family and who you want to appoint as guardian
              of your children.
            </p>
          </v-col>
        </v-row>
      </v-col>

      <v-col cols="12" md="7" class="mt-4">
        <div class="left-section-container">
          <div v-if="!loading">
            <v-row
              id="spouse"
              no-gutters
              class="static-people-box relation-section"
            >
              <v-col cols="12">
                <v-card class="elevation-10">
                  <SpouseOverview
                    :spouse="relations.spouse"
                    :testator="testator"
                  />
                </v-card>
              </v-col>
            </v-row>

            <v-row
              id="children"
              no-gutters
              class="static-people-box relation-section"
            >
              <v-col cols="12">
                <v-card class="elevation-10">
                  <ChildrenOverview
                    :relations="relations"
                    :testator="testator"
                  />
                </v-card>
              </v-col>
            </v-row>

            <v-row
              id="emergency-contacts"
              no-gutters
              class="static-people-box relation-section"
            >
              <v-col cols="12">
                <h2>Who do we contact upon your death?</h2>
                <p>
                  We will notify the following person/s in the event of your
                  passing.
                </p>
                <v-card class="elevation-10">
                  <EmergencyContactsOverview
                    :relations="relations"
                    :testator="testator"
                  />
                </v-card>
              </v-col>
            </v-row>

            <v-row
              id="executor"
              no-gutters
              class="static-people-box relation-section"
            >
              <v-col cols="12">
                <v-card class="elevation-10">
                  <ExecutorOverview
                    :relations="relations"
                    :testator="testator"
                  />
                </v-card>
              </v-col>
            </v-row>

            <v-col cols="12" class="mb-6">
              <h2>Optional people categories</h2>
              <p>
                Please consider the optional people categories below and
                complete those which are relevant to you.
              </p>
            </v-col>
            <v-row no-gutters class="static-people-box relation-section">
              <v-col cols="12">
                <v-card class="elevation-10">
                  <div class="specific-people-info-container">
                    <h2>Heirs<span class="optional">(Optional)</span></h2>
                    <ul>
                      <li>
                        These are people who you would like to leave an
                        inheritance to
                      </li>
                      <li>
                        You do not need to add your spouse/partner or children’s
                        details again
                      </li>
                    </ul>

                    <!-- Only on sm and up  -->
                    <div>
                      <PeopleList
                        :people="relations.heirs"
                        :testator="testator"
                        relation="HEIR"
                      />
                    </div>
                  </div>
                </v-card>
              </v-col>
            </v-row>
            <v-row no-gutters class="static-people-box relation-section">
              <v-col cols="12">
                <v-card class="elevation-10">
                  <div class="specific-people-info-container">
                    <h2>Charities<span class="optional">(Optional)</span></h2>
                    <ul>
                      <li>Tell us which charities you want to donate to</li>
                      <li>
                        Include the charity registration number if you know it
                      </li>
                    </ul>

                    <!-- Only on sm and up  -->
                    <div class="people-list">
                      <CharitiesList :charities="testator.charities" />
                    </div>
                  </div>
                </v-card>
              </v-col>
            </v-row>
            <v-row v-if="showTrusts" no-gutters class="relation-section">
              <v-col cols="12">
                <v-card class="elevation-10">
                  <div class="specific-people-info-container">
                    <h2>Trusts<span class="optional">(Optional)</span></h2>
                    <ul>
                      <li>Tell us which trusts you want to donate to</li>
                      <li>Include the trust registration number</li>
                    </ul>

                    <!-- Only on sm and up  -->
                    <div class="people-list">
                      <TrustsList :trusts="testatorTrusts" />
                    </div>
                  </div>
                </v-card>
              </v-col>
            </v-row>
            <div class="button-container">
              <GradientButton
                :title="'Save And Continue'"
                :small="true"
                :callout="'right'"
                @gradientButtonClicked="saveAndContinue"
              />
            </div>
          </div>

          <div v-else class="text-center">
            <v-progress-circular indeterminate color="primary" />
          </div>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import PeopleList from './PeopleList.vue';
  import CharitiesList from './CharitiesList.vue';
  import TrustsList from './TrustsList.vue';
  import SpouseOverview from './SpouseOverview.vue';
  import ChildrenOverview from './ChildrenOverview.vue';
  import ExecutorOverview from './ExecutorOverview.vue';
  import DeathContacts from './EmergencyContacts.vue';
  import { getRelations } from '../../constants/js/people';
  import { TESTATOR_QUERY } from '../../constants/graphql/testators';
  import EmergencyContactsOverview from './EmergencyContactsOverview.vue';

  export default {
    name: 'PeopleOverview',
    components: {
      PeopleList,
      CharitiesList,
      TrustsList,
      SpouseOverview,
      ChildrenOverview,
      ExecutorOverview,
      DeathContacts,
      EmergencyContactsOverview,
    },
    data() {
      return {
        testator: {},
        relations: {},
        loading: true,
      };
    },
    apollo: {
      testator: {
        query: TESTATOR_QUERY,
        variables() {
          return {
            id: this.testatorId,
          };
        },
        fetchPolicy: 'cache-and-network',
        result(response) {
          if (response.data && response.data.testator) {
            this.relations = getRelations(response.data.testator.people);
            this.loading = false;
          }
        },
      },
    },
    computed: {
      omOnlyExecutor() {
        if (this.testator.bypassOmOnlyExecutor) {
          return false;
        }
        return (this.testator.enabledFeatures || []).includes(
          'om_only_executor'
        );
      },
      spouseArray() {
        return this.relations.spouse.id ? [this.relations.spouse] : [];
      },
      showTrusts() {
        return this.testator.willCountry != 'GB';
      },
      testatorTrusts() {
        return this.testator.trusts.filter(
          trust => trust.testamentaryTrust !== 'true'
        );
      },
    },
    methods: {
      saveAndContinue() {
        this.$router.push({ name: 'OverviewPage', hash: '#your-people' });
      },
    },
  };
</script>

<style scoped lang="scss">
  .people-overview-container {
    text-align: left;
    padding-top: 40px;

    .standard-title-container {
      .your-people-description {
        font-size: 16px;
        font-weight: 400;
        line-height: 22px;
        text-align: left;
        color: #282828;
        padding-top: 20px;
      }
    }
    .static-people-box {
      // border in global.scss because of IE issue
    }

    .dynamic-people-box {
      // border in global.scss because of IE issue

      box-shadow: 2px 5px 4px #d5d5d5;
    }

    .main-people-image {
      max-height: 85%;
      max-width: 85%;
      margin-top: 20px;
    }

    .people-image {
      margin: 20px;
    }

    .relation-section {
      margin-bottom: 35px;

      .people-list {
        margin: 10px 0;
        width: 100%;
      }

      .divider {
        margin-top: 15px;
      }

      .optional {
        // color in global.scss because of IE issue
        font-size: 14px;
        padding-left: 10px;
      }
    }
  }
  .specific-people-info-container {
    padding: 20px 40px;

    p {
      font-weight: 500;
      margin-bottom: 0;
    }

    .edit-btn {
      margin-top: 12px;
      margin-bottom: 18px;
      margin-left: 0;
    }

    .person-info {
      margin-top: 2px;
      margin-bottom: 2px;
      padding-left: 3px;
    }

    h2 {
      margin-bottom: 7px;
    }

    .people-headings {
      margin-top: 3px;
      margin-bottom: 6px;
    }
  }
  ::v-deep .v-card {
    width: 100%;
  }
</style>
